/**
 * `POST /api/keystone/:accountId/generate` — the only call that produces
 * genuinely new intelligence state. The route ENQUEUES (`status: 'queued'` /
 * `'deduplicated'`, or `'excluded'` with a reason); generation runs in
 * Keystone's background, so a resolved mutation means "accepted", never
 * "intelligence is ready".
 *
 * Every intelligence reader's cache is marked stale with `refetchType:
 * 'none'` — nothing refetches against a still-empty account now; each reader
 * re-reads on its next mount (an invalidated query is stale regardless of
 * its `staleTime`, per query-core's `isStaleByTime`).
 */
import { useCallback } from 'react';
import { useMutation, useQueryClient, type MutateOptions } from '@tanstack/react-query';
import type { z } from 'zod';
import { toast } from 'sonner';
import { GenerateAccountStateResponseSchema } from '@clay-run/keystone-contract';
import { FLOW_ACCOUNT_INTEL_KEY, FLOW_ACCOUNT_INTEL_SUMMARY_KEY } from '../components/flow/contact/hooks';
import { KEYSTONE_STATE_KEY } from './useKeystoneState';
import { useApiFetchJson } from '../lib/apiFetchJson';

export type GenerateOutcome = z.infer<typeof GenerateAccountStateResponseSchema>;

export type GeneratePhase = 'idle' | 'pending' | 'accepted' | 'excluded' | 'error';

function phaseOf(mutation: { isPending: boolean; isError: boolean; data: GenerateOutcome | undefined }): GeneratePhase {
  if (mutation.isPending) return 'pending';
  if (mutation.isError) return 'error';
  if (mutation.data?.status === 'excluded') return 'excluded';
  if (mutation.data) return 'accepted';
  return 'idle';
}

/** Which phases accept a click: nothing in flight or just failed. */
function generateClickable(phase: GeneratePhase): boolean {
  return phase === 'idle' || phase === 'error';
}

/**
 * Toasts the generate outcome at the click site — the hook itself stays
 * data-only. Queued and deduplicated read the same to a rep.
 */
export function notifyGenerateOutcome(result: GenerateOutcome, queuedMessage = 'Refresh queued — back in 30–60s') {
  if (result.status === 'excluded') {
    toast.info(`Excluded from generation: ${result.reason}`);
  } else {
    toast.success(queuedMessage);
  }
}

/**
 * `accountId: null` (contact with no linked account) is never clickable.
 *
 * The mutation's variable IS the account it was fired for, so (1) the POST and
 * the cache invalidation always target the account the click was made on, even
 * if the caller has since re-pointed at another account, and (2) `phase` only
 * reflects the mutation when it was fired for the CURRENT account — a reused
 * component (row/panel switching accounts) shows the new account idle rather
 * than inheriting the old account's "Generating…". No effect or ref involved.
 */
export function useGenerateKeystoneState(accountId: string | null) {
  const fetchJson = useApiFetchJson();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (targetAccountId: string | null) => {
      if (targetAccountId === null) throw new Error('No account linked');
      return fetchJson(
        GenerateAccountStateResponseSchema,
        `/api/keystone/${encodeURIComponent(targetAccountId)}/generate`,
        { method: 'POST' },
      );
    },
    onSuccess: async (result, targetAccountId) => {
      switch (result.status) {
        case 'queued':
        case 'deduplicated':
          for (const root of [KEYSTONE_STATE_KEY, FLOW_ACCOUNT_INTEL_KEY, FLOW_ACCOUNT_INTEL_SUMMARY_KEY]) {
            const queryKey = [...root, targetAccountId];
            // A reader still in flight (e.g. the summary stream answering 404-empty,
            // cached fresh under `staleTime: Infinity`) would land AFTER the
            // invalidation and re-freshen a stale result. Cancel first: the entry
            // reverts to its pre-fetch state (a mid-stream partial is dropped), then
            // the invalidation marks it stale; the next mount re-reads.
            await queryClient.cancelQueries({ queryKey, exact: true });
            void queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
          }
          return;
        case 'excluded':
          // No job was enqueued and no callback will fire: no reader's cache
          // can change.
          return;
        default: {
          const unhandled: never = result;
          throw new Error(`Unhandled generate status: ${JSON.stringify(unhandled)}`);
        }
      }
    },
  });

  const { mutate: rawMutate } = mutation;
  const mutate = useCallback(
    (_variables?: undefined, options?: MutateOptions<GenerateOutcome, Error, string | null>) => rawMutate(accountId, options),
    [rawMutate, accountId],
  );

  const mine = mutation.variables === accountId;
  const phase: GeneratePhase = mine ? phaseOf(mutation) : 'idle';
  return { ...mutation, mutate, phase, clickable: accountId !== null && generateClickable(phase) };
}
